import { useEffect } from 'react';
import { tryGetAuthToken, isEmbedded } from '../lib/auth';

/**
 * DiagnosticBoundary — non-blocking. Renders children immediately and
 * runs an auth probe in the background, logging the result to the console
 * so DevTools always has the diagnostic. The user-visible state (info /
 * warning banner, retry cadence) is owned by App.tsx → SessionBanner.
 *
 * The previous version of this component was a full-page loader that
 * refused to pass children through until tokenOk && authedOk both
 * succeeded — when getSessionToken hung, the entire admin app was stuck
 * on "Initializing Shopify admin session…". That blocking behavior is
 * gone; the only thing this component does now is emit `[diag]` console
 * lines so future debugging has structured signal.
 */

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

interface ProbeResult {
  hasAppBridge: boolean;
  tokenOk: boolean;
  tokenError?: string;
  tokenLen?: number;
  publicOk: boolean;
  publicError?: string;
  windowOrigin: string;
  windowHref: string;
}

async function probe(): Promise<ProbeResult> {
  const hasAppBridge = isEmbedded();
  const windowOrigin = window.location.origin;
  const windowHref = window.location.href;
  console.info('[diag] starting probe: hasAppBridge=', hasAppBridge, 'API_BASE=', API_BASE);

  let tokenOk = false;
  let tokenError: string | undefined;
  let tokenLen: number | undefined;
  const token = await tryGetAuthToken();
  if (token) {
    tokenOk = true;
    tokenLen = token.length;
  } else {
    tokenError = 'getSessionToken returned null/timed out (see [auth] warnings)';
  }

  let publicOk = false;
  let publicError: string | undefined;
  try {
    const r = await fetch(`${API_BASE}/api/selector/years`, { credentials: 'omit' });
    publicOk = r.ok;
    if (!r.ok) publicError = `HTTP ${r.status}`;
  } catch (e) {
    publicError = (e as Error).message;
  }

  const result: ProbeResult = {
    hasAppBridge,
    tokenOk,
    tokenError,
    tokenLen,
    publicOk,
    publicError,
    windowOrigin,
    windowHref,
  };
  console.info('[diag] probe result:', result);
  return result;
}

export function DiagnosticBoundary({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    let cancelled = false;
    probe().then(() => {
      if (cancelled) return;
      // Result already logged; nothing else to do — App.tsx renders the banner.
    });
    return () => { cancelled = true; };
  }, []);

  return <>{children}</>;
}
